import {
  ChangeDetectorRef,
  Component,
  Injectable,
  OnDestroy,
  OnInit,
  Optional,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GSBaseControl } from './gs.base.control';
import { Item } from '../entity/entities';
import { FormCheckServeWindowRulesProperty } from '../entity/form/formCheckServeWindowRules.property';
import { CheckServeWindowRulesHelper } from '../helpers/checkServeWindowRules.helper';

@Component({
  selector: 'gs-check-serve-window-rules',
  templateUrl: './gs.checkServeWindowRules.control.html',
})
@Injectable()
export class GSCheckServeWindowRulesControl
  extends GSBaseControl
  implements OnInit, OnDestroy
{
  //#region -------Properties
  form: FormGroup<{ rules }>;

  addText = 'Click to add';
  editText = 'Click to edit the configured rules';

  minutesMin = CheckServeWindowRulesHelper.MinutesMin;
  minutesMax = CheckServeWindowRulesHelper.MinutesMax;
  anchorOptions = CheckServeWindowRulesHelper.anchorOptions;

  get rules(): FormArray {
    return this.form.get('rules') as FormArray;
  }

  get summary(): string {
    const stored = this.value;
    if (!stored || !stored.length) return null;

    return stored
      .map(
        (rule) =>
          `${rule.orderType || '?'}: ${rule.anchor || '?'} ${
            rule.minutes != null ? rule.minutes : '?'
          } min`
      )
      .join('; ');
  }

  get hasRules(): boolean {
    return !!(this.value && this.value.length);
  }

  get hasDuplicates(): boolean {
    return this.rules.controls.some((row, index) => this.isDuplicate(index));
  }

  get canApply(): boolean {
    return !!this.form && this.form.valid && !this.hasDuplicates;
  }
  //#endregion

  //#region -------Constructor
  constructor(
    @Optional() prop: FormCheckServeWindowRulesProperty,
    @Optional() item: Item,
    private changeDr: ChangeDetectorRef,
    private fb: FormBuilder
  ) {
    super(changeDr);
    this.property = prop;
    this.item = item;
  }
  //#endregion

  //#region -------NG Events
  ngOnInit(): void {
    this.buildForm();

    // undo on the screen calls item.revertData(), which replaces item.data wholesale - the form
    // has to be rebuilt from the reverted value, otherwise it keeps the discarded rows and
    // writes them back the next time the panel closes
    this.item.onRevertData.subscribe(this.dataReverted, this);
  }

  ngOnDestroy(): void {
    this.item?.onRevertData.unsubscribe(this.dataReverted);
  }
  //#endregion

  //#region -------UI Events
  addRule() {
    this.rules.push(this.buildRow(null));
  }

  removeRule(index: number) {
    this.rules.removeAt(index);
  }

  hide() {
    this.apply();
  }

  // one anchor per order type, so a repeated order type and anchor pair is ambiguous
  // and both rows holding it are flagged
  isDuplicate(index: number): boolean {
    const key = this.ruleKeyAt(index);
    if (!key) return false;

    return this.rules.controls.some(
      (row, i) => i != index && this.ruleKeyAt(i) == key
    );
  }
  //#endregion

  //#region -------Private
  private dataReverted = () => {
    this.buildForm();

    // a rule set that was invalid before the undo must not keep the field flagged
    this.property.onDoValidate.next(this.property);
    this.changeDr.detectChanges();
  };

  private buildForm() {
    const stored = this.value || [];

    this.form = this.fb.group({
      rules: this.fb.array(stored.map((rule) => this.buildRow(rule))),
    });
  }

  private buildRow(rule) {
    return this.fb.group({
      orderType: this.fb.control(rule ? rule.orderType : '', [
        Validators.required,
      ]),
      anchor: this.fb.control(rule ? rule.anchor : null, [Validators.required]),
      minutes: this.fb.control(rule ? rule.minutes : null, [
        Validators.required,
        Validators.min(this.minutesMin),
        Validators.max(this.minutesMax),
      ]),
    });
  }

  private ruleKeyAt(index: number): string {
    const row = this.rules.controls[index];

    return CheckServeWindowRulesHelper.ruleKey(
      row.get('orderType').value,
      row.get('anchor').value
    );
  }

  private apply() {
    const rules = this.rules.controls.map((row) => ({
      orderType: (row.get('orderType').value || '').trim(),
      anchor: row.get('anchor').value,
      minutes: row.get('minutes').value,
    }));

    const previousValue = this.value;
    this.value = rules;
    this.item.reCheckDirty();

    // the rule set is validated through ValidationTypes.CheckServeWindowRules, so an incomplete
    // row keeps the save button disabled through item.isValid
    this.property.onDoValidate.next(this.property);

    this.onChange.emit({
      propertyName: this.name,
      previousValue: previousValue,
      newValue: rules,
      item: this.item,
      property: this.property,
    });

    this.property.changed.emit({
      propertyName: this.property.name,
      newValue: rules,
      item: this.item,
      property: this.property,
    });
  }
  //#endregion
}
